import timecode from "smpte-timecode";

import type { SlateParagraph } from "../components/utils/types";

/**
 * Parses timecode string (hh:mm:ss.mmm)
 *
 * @param {string} text
 * @return {Object} smpte-timecode
 */
const singleString2tc = (text) => {
  let [s, mm, hh = "00"] = text.replace(/[[\]]/g, "").split(":").reverse();
  if (hh.length === 1) hh = `0${hh}`;
  if (mm.length === 1) mm = `0${mm}`;

  let [ss, mmm = "00"] = s.split(".");
  if (mmm.length > 2) mmm = mmm.substring(0, 2);
  if (mmm.length === 1) mmm = `${mmm}0`;

  // console.log(`${hh}:${mm}:${ss}:${mmm}`);

  let tc = null;
  try {
    tc = timecode(`${hh}:${mm}:${ss}:${mmm}`, 60);
  } catch (ignored) {}

  // console.log(tc.toString());
  return tc;
};

const string2tc = (text) => {
  const [a, b] = text.split("-");

  if (b) {
    return {
      start: singleString2tc(a),
      end: singleString2tc(b),
    };
  } else {
    return {
      start: singleString2tc(a),
      end: null,
    };
  }
};

/**
 * Timecode to time (seconds)
 *
 * @param {Object} tc smtpe-timecode
 * @return {number} seconds
 */
const tc2time = (tc) => ({
  start: tc.start.frameCount / 60,
  end: tc.end ? tc.end.frameCount / 60 : null,
});

/**
 * Timecode to string hh:mm:ss.mmm
 *
 * @param {Object} tc smtpe-timecode
 * @return {string} hh:mm:ss.mmm
 */
const singleTc2string = (tc) => {
  const [hh, mm, ss, mmm] = tc.toString().split(":");
  if (mmm === "00") return `${hh}:${mm}:${ss}`;

  return `${hh}:${mm}:${ss}.${mmm}0`;
};

const tc2string = (tc) => {
  if (tc.end) {
    return `${singleTc2string(tc.start)}-${singleTc2string(tc.end)}`;
  } else {
    return singleTc2string(tc.start);
  }
};

export const nakedTimecodeMatchers = [
  "(?:[01]\\d|2[0123]):(?:[012345]\\d):(?:[012345]\\d)\\.(?:\\d+)",
  "(?:[01]\\d|2[0123]):(?:[012345]\\d):(?:[012345]\\d)",
  "(?:\\d):(?:[012345]\\d):(?:[012345]\\d)",
  "(?:[012345]\\d):(?:[012345]\\d)",
  "(?:\\d):(?:[012345]\\d)",
];

// Just wrap them in [...]
const bracketedTimecodeMatchers = nakedTimecodeMatchers.map((m) => `\\[${m}\\]`);

export const dualNakedTimecodeMatchers = nakedTimecodeMatchers.map((m) => `${m}-${m}`);

// Just wrap them in [...]
const dualBracketedTimecodeMatchers = dualNakedTimecodeMatchers.map((m) => `\\[${m}\\]`);

// Enforce that the timecode is the only thing on the line
export const topLevelMatchers = [...bracketedTimecodeMatchers, ...dualBracketedTimecodeMatchers]
  .map((m) => `^${m}$`)
  .map((m) => new RegExp(m, "g"));

export const inlineMatchers = [
  ...bracketedTimecodeMatchers.map((m) => `${m}(?=\\s+)`),
  ...bracketedTimecodeMatchers.map((m) => `(?!^)${m}`),
].map((m) => new RegExp(m, "g"));

export const dualInlineMatchers = [
  ...dualBracketedTimecodeMatchers.map((m) => `${m}(?=\\s+)`),
  ...dualBracketedTimecodeMatchers.map((m) => `(?!^)${m}`),
].map((m) => new RegExp(m, "g"));

/**
 * Parses Slate object to metadata
 *
 * @param {Object} notes Slate internal state
 * @return {Object} metadata
 */
export const notes2metadata = (notes: SlateParagraph[]) => {
  const lines = notes.map(({ children }) => children.map(({ text }) => text).join("\n"));

  // noinspection UnnecessaryLocalVariableJS
  const segments = lines
    .reduce((acc, line, index) => {
      let matches = topLevelMatchers.map((m) => line.matchAll(m)).reduce((v, accum) => [...v, ...accum], []);

      if (index === 0 && matches.length === 0) matches = [["[00:00:00]"]];

      if (matches.length > 0) {
        const tc = string2tc(matches[0][0]);

        return [
          ...acc,
          {
            index,
            timecode: tc2string(tc),
            time: tc2time(tc),
            // line,
            lines: [],
            // matches,
          },
        ];
      }

      matches = [...inlineMatchers, ...dualInlineMatchers]
        .map((m) => line.matchAll(m))
        .reduce((v, accum) => [...v, ...accum], []);

      const timecodes = matches.map(([text]) => string2tc(text));

      const prev = acc.pop();
      prev.lines.push({
        index,
        line,
        empty: line === "",
        timecodes: timecodes.map((tc) => tc2string(tc)),
        times: timecodes.map((tc) => tc2time(tc)),
        matches,
        keywords: [...line.matchAll(/(^|[^\\])(\*\*|__)(?:(?:\r?\n|\r)(?!\r?\n|\r)|.)+?\2/g)],
      });

      return [...acc, prev];
    }, [])
    .map((segment, index) => {
      const title = segment.lines.find(({ line }) => line.match(/(^\s*)#+.+/m));
      const synopsis = segment.lines.filter(({ line }) => line.match(/^>(?:[\t ]*>)*/m));

      const nonNotes = [title?.index, ...synopsis.map(({ index: idx }) => idx)];
      const segmentNotes = segment.lines.filter(({ index: idx }) => !nonNotes.includes(idx));

      return {
        ...segment,
        timecodes: [
          ...segment.lines.flatMap(({ timecodes, times }) =>
            timecodes.map((timecode_, idx) => ({ timecode: timecode_, time: times[idx] }))
          ),
        ],
        title: title ? title.line.replace(/#/g, "").trim() : `Segment ${index + 1}`,
        synopsis: synopsis
          ? synopsis
              .map(({ line }) => line.replace(/^>|\*\*/g, "").trim())
              .join("\n")
              .replace(/\n\n+/g, "\n\n")
              .trim()
          : "",
        notes: segmentNotes
          ? segmentNotes
              .map(({ line }) =>
                line
                  .replace(/^\*\*.*\*\*$/, "")
                  .replace(/^>|\*\*/g, "")
                  .trim()
              )
              .join("\n")
              .replace(/\n\n+/g, "\n\n")
              .trim()
          : "",
        keywords: [
          ...new Set(
            segment.lines
              .flatMap(({ keywords }) => keywords)
              .map(([keyword]) => keyword.trim())
              .join(",")
              .replace(/\*/g, "")
              .split(",")
              .map((k) => k.trim())
          ),
        ].join(", "),
      };
    });

  // console.log({ segments });

  return segments;
};

/**
 * Converts metadata to Slate state
 *
 * @param {Object} metadata
 * @return {Object} Slate state
 */
export const metadata2notes = (metadata) =>
  metadata
    .sort(({ time: a }, { time: b }) => a - b)
    .map(({ timecode: timecode_, title, synopsis, notes, keywords }) => {
      const lines = [
        `[${timecode_}]`,
        `# ${title?.trim() ?? "Untitled"}`,
        synopsis
          .trim()
          .split("\n")
          .map((line) => `> ${line}`)
          .join("\n"),
        notes
          .trim()
          .split("\n")
          .map((line) => line)
          .join("\n"),
        keywords ? `**${keywords.trim()}**` : "",
      ].filter(Boolean);

      return lines.join("\n\n");
    })
    .join("\n")
    .split("\n")
    .map((text) => ({
      children: [{ text }],
    }));
